import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Plus, Edit, Trash2, Upload, Download, Save, X, Search } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function CostCenterMaster() {
  const { user } = useAuth();
  const canManage = !!user;
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const fileRef = useRef(null);

  const load = () => api.get("/cost-centers").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit?.name?.trim()) return toast.error("Name required");
    try {
      if (edit.id) await api.put(`/cost-centers/${edit.id}`, { name: edit.name });
      else await api.post("/cost-centers", { name: edit.name });
      toast.success("Saved");
      setEdit(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this cost center?")) return;
    await api.delete(`/cost-centers/${id}`);
    load();
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("pms_token");
    const r = await fetch(`${API}/cost-centers/template`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "cost_center_template.xlsx"; a.click();
    URL.revokeObjectURL(url);
  };

  const onImport = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/cost-centers/import", fd);
    toast.success(`Imported ${data.inserted} cost centers`);
    load();
  };

  const filtered = useMemo(
    () => list.filter((c) => !q.trim() || (c.name || "").toLowerCase().includes(q.trim().toLowerCase())),
    [list, q]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Masters</div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">Cost Center Master</h1>
          <p className="text-sm text-slate-500 mt-1">Classify indent spend by cost center for downstream reporting.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={downloadTemplate} data-testid="cc-template-btn" className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2"><Download size={14} /> Template</button>
          {canManage && (
            <>
              <input type="file" accept=".xlsx" ref={fileRef} hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) { onImport(f); e.target.value = ""; } }} />
              <button onClick={() => fileRef.current?.click()} data-testid="cc-import-btn" className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-md text-sm inline-flex items-center gap-2"><Upload size={14} /> Import Excel</button>
              <button onClick={() => setEdit({ name: "" })} data-testid="cc-add-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-3 py-2 rounded-md text-sm inline-flex items-center gap-2 font-semibold"><Plus size={14} /> Add</button>
            </>
          )}
        </div>
      </div>

      {edit && (
        <div className="bg-white border border-slate-200 rounded-lg p-4 flex items-end gap-3">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 font-semibold">Cost Center Name</div>
            <input data-testid="cc-name-input" autoFocus value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" />
          </div>
          <button onClick={save} data-testid="cc-save-btn" className="bg-[#1D2951] hover:bg-[#2B3A67] text-white px-4 py-2 rounded-md text-sm font-semibold inline-flex items-center gap-2"><Save size={14} /> Save</button>
          <button onClick={() => setEdit(null)} className="border border-slate-300 px-4 py-2 rounded-md text-sm inline-flex items-center gap-2"><X size={14} /> Cancel</button>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="px-6 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">All Cost Centers</div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="cc-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search cost center…"
              className="pl-9 pr-3 py-1.5 border border-slate-300 rounded-md text-sm w-72 focus:outline-none focus:ring-2 focus:ring-[#1D2951]/20 focus:border-[#1D2951]"
            />
          </div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <th className="text-left px-6 py-3">Name</th>
            <th className="text-left px-6 py-3">Created</th>
            {canManage && <th className="text-right px-6 py-3">Actions</th>}
          </tr></thead>
          <tbody>
            {filtered.length === 0 && <tr><td colSpan="3" className="text-center py-12 text-slate-400">{list.length === 0 ? "No cost centers" : "No matches for your search"}</td></tr>}
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-6 py-3 font-medium">{c.name}</td>
                <td className="px-6 py-3 font-mono text-xs text-slate-500">{c.created_at?.slice(0, 10)}</td>
                {canManage && (
                  <td className="px-6 py-3 text-right">
                    <button onClick={() => setEdit({ id: c.id, name: c.name })} className="p-1.5 hover:bg-slate-100 rounded text-slate-600 mr-1"><Edit size={14} /></button>
                    <button onClick={() => del(c.id)} className="p-1.5 hover:bg-rose-50 rounded text-rose-600"><Trash2 size={14} /></button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
